/**
 * Studio Team output parsing — isomorphic (imported by both the client
 * Roulette/StudioTeamPanel flow and server-side executors like
 * albumSessionExecutor.ts / bulkRunExecutor.ts).
 *
 * PAT (agent 11, the Executive Producer) compiles the final 5-6 Suno blocks
 * at the bottom of the 11-agent walkthrough.
 *
 * PRIMARY: sentinel markers (v2.6.24+ spec, §9). PAT wraps each block in
 * `<<<SUNO_BLOCK_N_...>>>` / `<<<END_BLOCK_N>>>`, anchored on the block
 * NUMBER only (the text after `SUNO_BLOCK_N_` up to `>>>` is descriptive
 * and ignored, so minor spec wording drift can't break the match the way
 * heading-text and fence-count changes repeatedly did). This makes block
 * identity explicit and unambiguous: no counting fences, no guessing a
 * block's identity from what its content looks like, and a block PAT
 * skips or leaves empty is detected as exactly that instead of shifting
 * every later block's label by one slot.
 *
 * FALLBACK: the pre-v2.6.24 fence + content-shape heuristics below, kept
 * verbatim for older spec versions or a run where PAT didn't comply with
 * the sentinel format. Earlier agents emit their own code fences along the
 * way (PHI's bar-count table, VOX's Character Dossier, etc.), so this path
 * can't just grab the first/last N fences in the whole document — it scopes
 * to PAT's section first, then falls back further to content-shape
 * matching when fence order/count alone isn't reliable. This class of bug
 * recurred repeatedly under the old fence-only approach (2026-08-08 "Walk
 * It On", 2026-08-17 python-fence repeat, 2026-08-24 missing-Style-fence
 * shift, 2026-08-25 Style/Excludes swap, 2026-08-27 two separate incidents
 * — see Forgejo #87/#91) — the sentinel path above exists specifically to
 * stop patching new instances of the same underlying fragility.
 *
 * This used to be two independently-maintained copies (client + server) —
 * the client one got fixed for the 2026-08-08 "Walk It On" corruption bug,
 * the server one didn't. Consolidated here (2026-08-13) so there is exactly
 * one implementation to keep correct.
 */

const BLOCK_LABELS: Record<number, string> = {
  1: 'Lyrics',
  2: 'Style',
  3: 'Excluded Style',
  4: 'Title / Artist',
  5: 'Summary',
  6: 'Image Prompt',
};

/** Matches a sentinel-delimited block, anchored on the block NUMBER only
 *  (via backreference) so descriptive text after `SUNO_BLOCK_N_` can vary
 *  without breaking the match, and a mismatched start/end number (PAT
 *  transposing digits) fails to match rather than silently pairing wrong. */
const SENTINEL_RE = /<<<\s*SUNO_BLOCK_([1-6])[^>\n]*>>>\s*\n?([\s\S]*?)<<<\s*END_BLOCK_\1\s*>>>/g;

/** Collects sentinel-delimited blocks from anywhere in `text` (no section
 *  scoping needed — the sentinel syntax is unique enough that a stray match
 *  elsewhere is effectively impossible). Last occurrence of a given block
 *  number wins, so a DEX REVISE loop that makes PAT recompile more than
 *  once in one response correctly keeps only the final compile. */
function collectSentinelBlocks(text: string): Map<number, string> {
  const found = new Map<number, string>();
  let m: RegExpExecArray | null;
  SENTINEL_RE.lastIndex = 0;
  while ((m = SENTINEL_RE.exec(text)) !== null) {
    found.set(Number(m[1]), cleanBlockBody(m[2]));
  }
  return found;
}

/**
 * Locates one sentinel-delimited block's content span as character offsets
 * into `text`, for callers that need to splice modified content back into
 * the original response (server-side length/style enforcement in
 * studioteam.routes.ts -- enforceStyleCap/enforceExcludesCap/enforceLyrics*)
 * rather than just read the extracted body. Uses the `d` (indices) regex
 * flag for exact capture-group offsets instead of manual index arithmetic.
 * Returns null if that block's sentinel pair isn't present in `text` --
 * callers should fall back to their own legacy header-based lookup.
 */
export function findSentinelBlockBounds(text: string, blockNum: number): { start: number; end: number; body: string } | null {
  const re = new RegExp(`<<<\\s*SUNO_BLOCK_${blockNum}[^>\\n]*>>>\\s*\\n?([\\s\\S]*?)<<<\\s*END_BLOCK_${blockNum}\\s*>>>`, 'gd');
  let m: RegExpExecArray | null;
  let last: RegExpExecArray | null = null;
  while ((m = re.exec(text)) !== null) last = m;
  const indices = (last as unknown as { indices?: Array<[number, number]> } | null)?.indices;
  if (!last || !indices?.[1]) return null;
  const [start, end] = indices[1];
  return { start, end, body: last[1] };
}

/** Strip block-internal noise: stray ``` fences, character-count preamble lines, "Copy" artifacts. */
function cleanBlockBody(raw: string): string {
  let s = raw.trim();
  s = s.replace(/^\s*[\d,]+\s*\/\s*[\d,]+(?:\s*[–—-]\s*[\d,]+)?[^\n]*\n/, '');
  s = s.replace(/```[a-zA-Z]*\s*\n?/g, '').replace(/```/g, '');
  s = s.replace(/^\s*Copy\s*$/gim, '');
  return s.trim();
}

/** Matches a fenced code block, requiring the closing fence to use the same
 *  backtick count as the opener (via backreference) so 3- and 4-backtick
 *  fences never cross-match each other. */
const FENCE_RE = /(`{3,4})[a-zA-Z]*\n([\s\S]*?)\1/g;

/** Scope `text` down to PAT's compiled section, stopping before Caption
 *  Options if present. Returns the full text unchanged if nothing usable is
 *  found at all (unexpected/malformed response).
 *
 *  PAT's own reasoning/checklist (before the real compile) can itself
 *  contain fenced code -- e.g. a 2026-08-17 run had PAT literally
 *  demonstrate its character-count verification as a ```python fence
 *  ("len(lyrics_block_1) = 4,892 characters") before compiling the real
 *  blocks. Any such stray fence shifts every subsequent block's position
 *  by one. When a "FINAL OUTPUT" marker is present (it usually is, in
 *  varying wording -- "FINAL OUTPUT — SUNO BLOCKS 1-5", "FINAL OUTPUT —
 *  SIX SUNO BLOCKS", etc.), it reliably sits *after* PAT's reasoning and
 *  *before* the real blocks -- strictly more precise than an agent-heading
 *  anchor since it also skips PAT's own preamble, not just the earlier
 *  agents' sections. Takes the LAST such marker in the document, since a
 *  DEX REVISE loop (§1.13.3) can make PAT recompile more than once in a
 *  single response -- only the final compile is the one actually shipped.
 *
 *  Historically this scoped to "everything after the last agent-numbered
 *  heading" on the theory that PAT (agent 11) was always the final agent to
 *  get a heading. That assumption broke the day DEX (agent 13, Phase 9)
 *  went from off-by-default to on-by-default (v2.6.22 of the prompt spec):
 *  DEX's own heading now routinely follows PAT's, which made "last heading"
 *  scope straight past PAT's compiled blocks into DEX's critique section --
 *  no real Suno blocks there at all, and extraction silently fell through
 *  to the whole-document fallback and grabbed whatever fences it could find.
 *  FINAL OUTPUT is checked first and globally (not pre-scoped to a heading)
 *  specifically so it no longer depends on PAT being the last agent to
 *  speak. The agent-numbered-heading scan is now only a fallback for the
 *  rare response with no FINAL OUTPUT marker at all, and even then the
 *  result is truncated at the *next* heading after it (DEX's, if present)
 *  so DEX's section is never scanned for fences either way. */
function scopeToFinalAgentSection(text: string): string {
  const headingRe = /^#{1,4}\s*\[\d{1,2}\][^\n]*$/gm;
  const finalOutputReGlobal = /^#{1,4}[^\n]*FINAL OUTPUT[^\n]*$/gim;

  let lastFinalOutputEnd = -1;
  let fm: RegExpExecArray | null;
  while ((fm = finalOutputReGlobal.exec(text)) !== null) {
    lastFinalOutputEnd = fm.index + fm[0].length;
  }

  let section: string;
  if (lastFinalOutputEnd > -1) {
    section = text.slice(lastFinalOutputEnd);
  } else {
    let lastHeadingEnd = -1;
    let hm: RegExpExecArray | null;
    headingRe.lastIndex = 0;
    while ((hm = headingRe.exec(text)) !== null) {
      lastHeadingEnd = hm.index + hm[0].length;
    }
    section = lastHeadingEnd > -1 ? text.slice(lastHeadingEnd) : text;
  }

  // Stop at the next agent-numbered heading (e.g. DEX's critique heading,
  // which now commonly follows PAT's compile) so that section never bleeds
  // into it, regardless of which branch above produced `section`.
  headingRe.lastIndex = 0;
  const nextHeadingMatch = headingRe.exec(section);
  if (nextHeadingMatch) {
    section = section.slice(0, nextHeadingMatch.index);
  }

  const captionMatch = section.match(/^#{1,4}\s*Caption Options/im);
  if (captionMatch?.index !== undefined) section = section.slice(0, captionMatch.index);

  return section;
}

/** Fenced bodies this short are never a real block (the shortest legitimate
 *  block, Title/Artist, runs 50+ chars) -- almost certainly a stray
 *  demonstration/example fence in PAT's preamble that slipped past the
 *  FINAL OUTPUT anchor (e.g. no such marker existed in that run at all).
 *  Defense-in-depth alongside the anchor above, not a replacement for it. */
const MIN_PLAUSIBLE_BLOCK_LENGTH = 25;

/** A short fence that reads like PAT literally demonstrating its own
 *  verification work (e.g. `len(lyrics_block_1) = 4,892 characters ✓ PASS`)
 *  rather than a real output block. Every prompt-spec change that adds more
 *  "log this in originalData" / checklist bookkeeping increases the odds PAT
 *  renders one of these visibly -- and a single stray fence here shifts every
 *  block after it by one position (the 2026-08-08 "Walk It On" bug, and the
 *  2026-08-17 python-fence repeat of it). Dropped before slotting so it can
 *  never occupy a block position at all. */
function looksLikeStrayDemoFence(body: string): boolean {
  return body.length < 300 && /len\(|char(?:acter)? count|✓|\bPASS\b|\bFAIL\b|verification/i.test(body);
}

function collectFencedBodies(text: string): string[] {
  const bodies: string[] = [];
  let m: RegExpExecArray | null;
  FENCE_RE.lastIndex = 0;
  while ((m = FENCE_RE.exec(text)) !== null) {
    const body = cleanBlockBody(m[2]);
    if (body.length >= MIN_PLAUSIBLE_BLOCK_LENGTH && !looksLikeStrayDemoFence(body)) {
      bodies.push(body);
    }
  }
  return bodies;
}

// ---------------------------------------------------------------------------
// Content-signature matching -- a defense against positional drift.
//
// extractBlocks() used to assign fenced bodies to labels purely by their
// order after PAT's FINAL OUTPUT marker: 1st fence = Lyrics, 2nd = Style,
// 3rd = Excluded Style, etc. That's fragile to exactly one thing: any extra
// stray fence anywhere in the sequence silently shifts every label after it
// by one slot, with no error -- Excludes-list content lands in Style,
// Title/Artist lands in Excluded Style, and so on down the line. Each of the
// 5 strongly-shaped blocks below gets matched by what it actually looks like
// first; only the shapeless one (Summary, plain prose) falls back to
// positional order among whatever's left.
// ---------------------------------------------------------------------------

const EXCLUDE_BOILERPLATE_MARKERS = [
  'glossy pop polish',
  'plastic midi drums',
  'no wordless vocals',
  'no background chanting',
  'breathy tiktok delivery',
];

/**
 * NIX §5.5 stop-signal clause (STUDIO_TEAM_v2_6_20_PATCH_RUNTIME.md, Graft 4).
 * Suno's [end] tag is unreliable alone -- the model leans on an outro
 * description plus an audible resolution cue (fade or silence) to recognize
 * "stop here." Banning fade/ending language in Excludes removes that cue and
 * was the direct, replicated cause of a runaway render that stretched to the
 * ~8-minute ceiling. Warn, never block -- a DECLINE-state deviation (a
 * deliberate hard-cut ending, described explicitly in the outro tag) is
 * legal when logged, same as every other gate in this system.
 */
const FADE_EXCLUDE_MARKERS = ['fade out', 'slow fade', 'fade', 'outro', 'ending'];

function looksLikeTitleArtist(body: string): boolean {
  return /^\s*Title\s*[:—]/im.test(body) && body.length < 500;
}

function looksLikeLyrics(body: string): boolean {
  return /\[[^\]]+\]/.test(body) && body.length > 800;
}

/** Strong signature only: the fixed anti-AI-sheen/Anti-Hum boilerplate
 *  phrases that verbatim appear in nearly every real Excludes block. Checked
 *  early because it's genuinely unambiguous -- no other block ever contains
 *  these exact phrases. */
function looksLikeExcludedStyleStrong(body: string): boolean {
  if (looksLikeTitleArtist(body)) return false;
  const lower = body.toLowerCase();
  return EXCLUDE_BOILERPLATE_MARKERS.some(marker => lower.includes(marker));
}

/** Weak fallback only: a bare comma-separated list with no semicolons. This
 *  is NOT reliable enough to out-rank Style's own signature -- a real Style
 *  block that simply doesn't hit 4+ semicolons that generation (a shorter or
 *  differently-formatted one) looks exactly like this too, and stealing it
 *  here is precisely what caused the 2026-08-25 Style<->Excludes swap: this
 *  fallback ran before looksLikeStyle got a chance, grabbed the real Style
 *  body, and pushed the real Excludes content into Style's now-empty slot.
 *  Must only be tried after looksLikeStyle has already had first pick. */
function looksLikeExcludedStyleWeak(body: string): boolean {
  if (looksLikeTitleArtist(body)) return false;
  // gear:/mastering: are the fixed final two categories of the Style
  // template -- a body carrying either is Style even if it happens to have
  // fewer than 4 semicolons that generation, never Excludes.
  if (/mastering:|gear:/i.test(body)) return false;
  const commaCount = (body.match(/,/g) ?? []).length;
  return !body.includes(';') && body.length < 900 && commaCount > 5;
}

/**
 * `gear:` and `mastering:` are the fixed final two categories of the Style
 * template (BLOCK 2) -- they never appear in any other block, regardless of
 * how many semicolons a given generation's Style string actually used.
 * That makes their presence the single most reliable Style signal, more
 * reliable than counting semicolons: a real Style body that deviates from
 * the semicolon-categorized template (fewer than 4 that run) still carries
 * these, and relying on the keyword alone means it can't be shadowed by a
 * weaker Summary/Excludes fallback that happens to also fire on it.
 */
function looksLikeStyle(body: string): boolean {
  if (looksLikeTitleArtist(body)) return false;
  return /mastering:|gear:/i.test(body) && body.length < 1200;
}

function looksLikeImagePrompt(body: string): boolean {
  const t = body.trim();
  return t.startsWith('{') && t.endsWith('}');
}

/** Weakest signature of the six -- plain prose has no distinctive marker of
 *  its own, so this only asserts what Summary is *not* (no section tags, not
 *  JSON, not semicolon-heavy like Style, not a bare comma list like Excludes)
 *  plus a sentence-ending punctuation check. Runs last, after every stronger
 *  signature has had first pick, specifically so that when one whole block is
 *  missing (e.g. PAT never fenced Style at all) the real Summary body still
 *  lands in Summary's slot instead of being blind-filled into Style's. */
function looksLikeSummary(body: string): boolean {
  if (looksLikeTitleArtist(body)) return false;
  if (/\[[^\]]+\]/.test(body)) return false;
  if (body.trim().startsWith('{')) return false;
  if (/mastering:|gear:/i.test(body)) return false; // Style-only markers, never narrative prose
  if ((body.match(/;/g) ?? []).length >= 4) return false;
  const commaCount = (body.match(/,/g) ?? []).length;
  if (commaCount > 5 && !/[.!?]/.test(body)) return false;
  return body.length > 40 && body.length < 3000 && /[.!?]\s*$/.test(body.trim());
}

/** Assign fenced bodies to the 6 canonical labels by content shape where the
 *  shape is unambiguous, falling back to original fence order for whatever's
 *  left. Silently degrades to positional-only behavior when none of the
 *  signatures fire, so this never does worse than the old algorithm. */
function reconcileByContent(bodies: string[]): string[] {
  const used = new Set<number>();
  const assigned: Record<number, string> = {};

  const tryAssign = (label: number, matcher: (b: string) => boolean) => {
    if (label in assigned) return; // already claimed by a stronger/earlier matcher
    for (let i = 0; i < bodies.length; i++) {
      if (used.has(i)) continue;
      if (matcher(bodies[i])) {
        assigned[label] = bodies[i];
        used.add(i);
        return;
      }
    }
  };

  // Strongest/least ambiguous signatures first, so a weaker later matcher
  // can't steal a body a stronger one should have claimed. Style's
  // semicolon+keyword check runs BEFORE Excluded Style's weak comma-list
  // fallback specifically so a real Style block never gets stolen by it
  // (see looksLikeExcludedStyleWeak's comment for the incident this fixes).
  tryAssign(1, looksLikeLyrics);
  tryAssign(4, looksLikeTitleArtist);
  tryAssign(3, looksLikeExcludedStyleStrong);
  tryAssign(2, looksLikeStyle);
  tryAssign(6, looksLikeImagePrompt);
  tryAssign(5, looksLikeSummary);
  tryAssign(3, looksLikeExcludedStyleWeak);

  const remainingLabels = [1, 2, 3, 4, 5, 6].filter(l => !(l in assigned));
  const remainingBodies = bodies.filter((_, i) => !used.has(i));
  remainingLabels.forEach((label, idx) => {
    if (remainingBodies[idx] !== undefined) assigned[label] = remainingBodies[idx];
  });

  return [1, 2, 3, 4, 5, 6].map(l => assigned[l] ?? '');
}

export function extractBlocks(text: string): { label: string; body: string }[] {
  // Primary path: explicit sentinel markers (§9, v2.6.24+). Block identity
  // comes from the marker's own number, not fence order or content shape,
  // so none of the misassignment classes below (missing fence, transposed
  // order, content that happens to look like another block) can occur here.
  const sentinelBlocks = collectSentinelBlocks(text);
  if (sentinelBlocks.size >= 4) {
    return [1, 2, 3, 4, 5, 6]
      .map(i => ({ label: BLOCK_LABELS[i], body: sentinelBlocks.get(i) ?? '' }))
      .filter(b => b.body.length > 0);
  }

  // Fallback: pre-v2.6.24 fence + content-shape heuristics (no/too few
  // sentinels found -- an older spec version, or PAT didn't comply).
  const section = scopeToFinalAgentSection(text);
  const bodies = collectFencedBodies(section);

  if (bodies.length >= 4) {
    const ordered = reconcileByContent(bodies);
    return ordered
      .map((body, i) => ({ label: BLOCK_LABELS[i + 1] ?? `Block ${i + 1}`, body }))
      .filter(b => b.body.length > 0);
  }

  // Last-resort fallback: whole-document scan, for the pathological case
  // where no agent heading was found (so scopeToFinalAgentSection was a
  // no-op) and PAT's section didn't yield enough fences on its own.
  const allBlocks = collectFencedBodies(text);
  const tail = reconcileByContent(allBlocks.slice(-6));
  return tail
    .map((body, i) => ({ label: BLOCK_LABELS[i + 1] ?? `Block ${i + 1}`, body }))
    .filter(b => b.body.length > 0);
}

/** Pull "Title: X" / "Artist: Y" out of Pat's Block 4 text. */
export function parseTitleArtist(body: string): { title?: string; artist?: string } {
  const titleM = body.match(/^\s*Title\s*[:—]\s*(.+)$/im);
  const artistM = body.match(/^\s*Artist\s*[:—]\s*(.+)$/im);
  return {
    title: titleM?.[1]?.trim().replace(/^["']|["']$/g, ''),
    artist: artistM?.[1]?.trim().replace(/^["']|["']$/g, ''),
  };
}

export interface StudioTeamBlocks {
  title: string;
  artist: string;
  style: string;
  excludedStyle: string;
  lyrics: string;
  summary: string;
  imagePrompt: string;
}

/** Parse a full Studio Team response text into the 6 named blocks. Missing blocks come back as empty strings. */
export function parseStudioTeamOutput(text: string, fallbackTitle = 'Untitled'): StudioTeamBlocks {
  const blocks = extractBlocks(text);
  const blockBy = new Map<string, string>(blocks.map(b => [b.label, b.body]));
  const titleArtist = blockBy.get('Title / Artist') ?? '';
  const { title: parsedTitle, artist: parsedArtist } = parseTitleArtist(titleArtist);
  return {
    title: parsedTitle || fallbackTitle,
    artist: parsedArtist || '',
    style: blockBy.get('Style') ?? '',
    excludedStyle: blockBy.get('Excluded Style') ?? '',
    lyrics: blockBy.get('Lyrics') ?? '',
    summary: blockBy.get('Summary') ?? '',
    imagePrompt: blockBy.get('Image Prompt') ?? '',
  };
}

/**
 * Cheap shape checks that catch block cross-contamination (wrong content
 * landing in the wrong field) even when extraction technically "succeeded"
 * (found >= 4 fenced blocks). Doesn't prove the content is *right* — just
 * flags content that's clearly the *wrong kind of thing* for its field, so a
 * future prompt-spec wording change fails loud instead of silently saving
 * garbage like the 2026-08-08 "Walk It On" incident (PHI's bar-count table
 * ended up in Lyrics, VOX's Character Dossier ended up in Excluded Style,
 * PAT's real Title/Artist block ended up in Summary).
 */
export function validateBlocks(blocks: StudioTeamBlocks): string[] {
  const issues: string[] = [];

  if (blocks.lyrics && !/\[[^\]]+\]/.test(blocks.lyrics)) {
    issues.push('Lyrics has no [Section] tags — likely grabbed the wrong block.');
  }
  // AUTO-105: 1000 is Suno's own Excludes cap (the code trims to 950). The old 600 flagged ordinary lists of 600-950
  // characters as "a Character Dossier"; real dossiers are still caught by their "Age/Gender / Timbre / ..." lines.
  if (blocks.excludedStyle && (blocks.excludedStyle.length > 1000 || /^\s*\[?(Age\/Gender|Timbre|Delivery|Accent)/im.test(blocks.excludedStyle))) {
    issues.push('Excluded Style looks like a Character Dossier, not a short exclude list — likely grabbed the wrong block.');
  }
  if (/^\s*Title\s*[:—]/im.test(blocks.excludedStyle)) {
    issues.push('Excluded Style looks like a Title/Artist block — likely grabbed the wrong block.');
  }
  if (/^\s*Title\s*[:—]/im.test(blocks.summary)) {
    issues.push('Summary looks like a Title/Artist block, not narrative prose — likely grabbed the wrong block.');
  }
  if (blocks.style && !blocks.style.includes(';')) {
    issues.push('Style has no semicolon-separated categories — likely grabbed the wrong block.');
  }
  if (blocks.style && EXCLUDE_BOILERPLATE_MARKERS.some(marker => blocks.style.toLowerCase().includes(marker))) {
    issues.push('Style contains standard Excludes boilerplate (e.g. "glossy pop polish") — likely grabbed the Excluded Style block instead.');
  }
  if (!blocks.style) {
    issues.push('Style is empty — PAT likely never fenced a Style block in this response.');
  }
  if (!blocks.artist) {
    issues.push('Artist is empty — Title/Artist block likely failed to parse.');
  }
  if (FADE_EXCLUDE_MARKERS.some(marker => blocks.excludedStyle.toLowerCase().includes(marker))) {
    issues.push(
      'Excluding fade/ending language removes Suno\'s stop signal and is the documented ' +
      'cause of 7:59 runaway renders (v2.6.20 NIX §5.5 — the stop-signal clause).'
    );
  }

  return issues;
}
